import * as React from 'react';
import ActivationOpsSections from './jobs/ActivationOpsSections';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import useActivation, {
    ActivationObject,
} from '@/src/Activations/components/hooks/useActivation';
import useShare from '@/src/Share/components/hooks/useShare';
import useGetGalleries from '@/src/GalleryConfiguration/components/hooks/useGetGalleries';
import useWhitelabelSettings from '@/src/WhitelabelSettings/components/hooks/useWhitelabelSettings';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useEditActivation from '@/src/Activations/components/hooks/useEditActivation';
import useActivationOrgReassignment from '@/src/Activations/components/hooks/useActivationOrgReassignment';
import useOrgs from '@/src/Orgs/components/hooks/useOrgs';
import FeatureFlagContext from '@/src/Features/components/FeatureFlagContext';
import { FeatureFlags } from '@/src/Features/lib/constants';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import HeroChart, {
    AxisTick,
    ChartSeries,
} from '../components/HeroChart';
import EventDevices from '../components/EventDevices';

/*
 * Overview v2 — the event landing (dashboard archetype) on live data:
 * identity line, KPI row (photos / sessions / shares / views), gallery
 * links, and recent sessions. The hero chart joins when a timeseries
 * endpoint exists; deeper tools stay one dock-tap away in classic.
 */

interface SessionRow {
    id: string | number;
    photoSessionId?: string | number;
    date: string;
    createdAt?: string;
    total: number;
    destination?: string;
}

export default function OverviewV2() {
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const navigate = useNavigate();
    const { getActivation, getActivationSessions, unarchiveActivation } =
        useActivation();
    const { reassignActivationOrg } = useActivationOrgReassignment();
    const { listOrgs } = useOrgs();
    const featureFlags = useContext(FeatureFlagContext) as any;
    const canReassign = !!featureFlags?.[FeatureFlags.ACTIVATION_ORG_SWAP_V1];
    const { getSharesForActivation } = useShare();
    const { getGalleriesForActivation } = useGetGalleries();
    const { getOrgDomain } = useWhitelabelSettings();
    const { org } = useUserOrg();
    const { saveActivation } = useEditActivation(id);

    const [activation, setActivation] = useState<ActivationObject | null>(
        null,
    );
    const [sessions, setSessions] = useState<SessionRow[]>([]);
    const [shares, setShares] = useState<any[]>([]);
    const [galleries, setGalleries] = useState<any[]>([]);
    const [galleryUrl, setGalleryUrl] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState({
        name: '',
        date: '',
        location: '',
        v3Folder: '',
    });
    const [reassignOpen, setReassignOpen] = useState(false);
    const [reassignOrgs, setReassignOrgs] = useState<any[] | null>(null);
    const [reassignTarget, setReassignTarget] = useState('');
    const [reassignBusy, setReassignBusy] = useState(false);
    const [isSavingDetails, setIsSavingDetails] = useState(false);
    const [detailsError, setDetailsError] = useState(false);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const [a, s, sh, g, base] = await Promise.all([
                    getActivation(id),
                    getActivationSessions(id),
                    getSharesForActivation(id),
                    getGalleriesForActivation(id),
                    getOrgDomain(org?.id),
                ]);
                if (!mounted) return;
                setActivation(a);
                setSessions(s ?? []);
                setShares(sh ?? []);
                setGalleries(g ?? []);
                setGalleryUrl(base ?? '');
            } catch (e) {
                /* partial data is fine for v1 */
            } finally {
                if (mounted) setIsLoading(false);
            }
        };
        load();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    /* both endpoints LEFT JOIN one-to-many tables, so a session with two
       shares (or a share with two media rows) comes back duplicated —
       dedupe before counting or charting */
    const uniqueSessions = useMemo(() => {
        const seen = new Set<string | number>();
        return sessions.filter((s) => {
            const key = s.photoSessionId ?? s.id;
            if (key == null) return true;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [sessions]);

    const uniqueShares = useMemo(() => {
        const seen = new Set<string | number>();
        return shares.filter((s: any) => {
            if (s.id == null) return true;
            if (seen.has(s.id)) return false;
            seen.add(s.id);
            return true;
        });
    }, [shares]);

    const kpis = useMemo(() => {
        const photos = uniqueSessions.reduce(
            (sum, s) => sum + (Number(s.total) || 0),
            0,
        );
        const unsent = uniqueShares.filter((s: any) => !s.sent).length;
        const views = galleries.reduce(
            (sum, g: any) => sum + (Number(g.totalViews) || 0),
            0,
        );
        return {
            photos,
            sessions: uniqueSessions.length,
            shares: uniqueShares.length,
            unsent,
            views,
        };
    }, [uniqueSessions, uniqueShares, galleries]);

    const recentSessions = useMemo(
        () =>
            [...uniqueSessions]
                .sort(
                    (a, b) =>
                        moment(b.createdAt ?? b.date).valueOf() -
                        moment(a.createdAt ?? a.date).valueOf(),
                )
                .slice(0, 5),
        [uniqueSessions],
    );

    /* hero chart: cumulative photos + shares, bucketed hourly for
       single-day events and daily for longer ones */
    const chart = useMemo(() => {
        const events = (
            rows: any[],
            value: (r: any) => number,
            ts: (r: any) => any,
        ) =>
            rows
                .map((r) => ({ t: moment(ts(r)).valueOf(), v: value(r) }))
                .filter((e) => Number.isFinite(e.t) && e.t > 0);

        const photoEvents = events(
            uniqueSessions,
            (s) => Number(s.total) || 0,
            (s) => s.createdAt ?? s.date,
        );
        const shareEvents = events(
            uniqueShares,
            () => 1,
            (s) => s.createdAt ?? s.created_at,
        );
        const all = [...photoEvents, ...shareEvents];
        if (all.length === 0) return null;

        const minT = Math.min(...all.map((e) => e.t));
        const maxT = Math.max(...all.map((e) => e.t));
        const unit: 'hour' | 'day' =
            maxT - minT <= 48 * 3600 * 1000 ? 'hour' : 'day';

        const buckets: number[] = [];
        const indexOf = new Map<number, number>();
        const cursor = moment(minT).startOf(unit);
        const last = moment(maxT).startOf(unit).valueOf();
        while (cursor.valueOf() <= last && buckets.length < 500) {
            indexOf.set(cursor.valueOf(), buckets.length);
            buckets.push(cursor.valueOf());
            cursor.add(1, unit);
        }
        if (buckets.length < 2) return null;

        const cumulative = (evts: { t: number; v: number }[]) => {
            const per = buckets.map(() => 0);
            evts.forEach((e) => {
                const i = indexOf.get(moment(e.t).startOf(unit).valueOf());
                per[i ?? buckets.length - 1] += e.v;
            });
            let run = 0;
            return per.map((v) => (run += v));
        };

        const series: ChartSeries[] = [
            {
                label: 'Photos',
                color: 'hsl(var(--brand))',
                values: cumulative(photoEvents),
            },
        ];
        if (shareEvents.length > 0) {
            series.push({
                label: 'Shares',
                color: 'hsl(var(--attention))',
                values: cumulative(shareEvents),
            });
        }

        const fresh = Date.now() - maxT < 3600 * 1000;
        const axis: AxisTick[] = [0, 1, 2, 3, 4].map((k) => {
            const i = Math.round((k * (buckets.length - 1)) / 4);
            const isLast = i === buckets.length - 1;
            return {
                label:
                    isLast && fresh
                        ? 'now'
                        : moment(buckets[i]).format(
                              unit === 'hour' ? 'h A' : 'MMM D',
                          ),
                strong: isLast && fresh,
            };
        });

        return { series, axis };
    }, [uniqueSessions, uniqueShares]);

    const d = activation ? moment(activation.date) : null;

    const startEditing = () => {
        if (!activation) return;
        setDraft({
            name: activation.name ?? '',
            date: moment(activation.date).format('YYYY-MM-DD'),
            location: activation.location ?? '',
            v3Folder: (activation as any).v3Folder ?? '',
        });
        setReassignOpen(false);
        setDetailsError(false);
        setIsEditing(true);
    };

    const saveDetails = async () => {
        if (!activation || isSavingDetails || !draft.name.trim()) return;
        setIsSavingDetails(true);
        setDetailsError(false);
        const ok = await saveActivation({
            name: draft.name.trim(),
            date: draft.date,
            location: draft.location.trim(),
            v3Folder: draft.v3Folder.trim() || null,
        }).catch(() => false);
        setIsSavingDetails(false);
        if (ok) {
            setActivation({
                ...activation,
                name: draft.name.trim(),
                date: draft.date as any,
                location: draft.location.trim(),
                v3Folder: draft.v3Folder.trim(),
            } as any);
            setIsEditing(false);
        } else {
            setDetailsError(true);
        }
    };

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-16 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    return (
        <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
            {/* title block — the page owns the event name; the crumb is
                section-only ("Activations"). Details edit inline here —
                the classic details tab's day job. */}
            {isEditing ? (
                <div className="mb-9 mt-2 max-w-[560px]">
                    <input
                        autoFocus
                        className="ps-input w-full text-[23px] font-bold leading-tight tracking-[-0.02em]"
                        value={draft.name}
                        placeholder="Activation name"
                        onChange={(e) =>
                            setDraft((v) => ({ ...v, name: e.target.value }))
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') saveDetails();
                            if (e.key === 'Escape') setIsEditing(false);
                        }}
                    />
                    <div className="mt-3 flex flex-wrap items-end gap-x-5 gap-y-2">
                        <div>
                            <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">
                                Date
                            </label>
                            <input
                                type="date"
                                className="ps-input text-[13px]"
                                value={draft.date}
                                onChange={(e) =>
                                    setDraft((v) => ({
                                        ...v,
                                        date: e.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div className="min-w-[200px] flex-1">
                            <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">
                                Location
                            </label>
                            <input
                                className="ps-input w-full text-[13px]"
                                value={draft.location}
                                placeholder="Venue, city"
                                onChange={(e) =>
                                    setDraft((v) => ({
                                        ...v,
                                        location: e.target.value,
                                    }))
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveDetails();
                                    if (e.key === 'Escape')
                                        setIsEditing(false);
                                }}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">
                                v3 folder{' '}
                                <span className="font-normal text-faint">
                                    — optional
                                </span>
                            </label>
                            <input
                                className="ps-input w-[180px] font-mono text-[12px]"
                                value={draft.v3Folder}
                                onChange={(e) =>
                                    setDraft((v) => ({
                                        ...v,
                                        v3Folder: e.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div className="flex items-center gap-2.5">
                            {detailsError && (
                                <span className="text-[11.5px] font-semibold text-attention">
                                    Save failed
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={saveDetails}
                                disabled={isSavingDetails || !draft.name.trim()}
                                className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-white disabled:opacity-50"
                            >
                                {isSavingDetails ? 'Saving…' : 'Save'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsEditing(false)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-muted-foreground hover:text-foreground"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                    {canReassign && (
                        <div className="mt-3">
                            {reassignOpen ? (
                                <div className="flex flex-wrap items-center gap-2.5">
                                    <select
                                        className="ps-input w-[220px] cursor-pointer appearance-none text-[12.5px]"
                                        value={reassignTarget}
                                        onChange={(e) =>
                                            setReassignTarget(e.target.value)
                                        }
                                    >
                                        <option value="">
                                            Move to organization…
                                        </option>
                                        {(reassignOrgs ?? [])
                                            .filter(
                                                (o: any) =>
                                                    o.id !== org?.id,
                                            )
                                            .map((o: any) => (
                                                <option
                                                    key={o.id}
                                                    value={o.id}
                                                >
                                                    {o.name}
                                                </option>
                                            ))}
                                    </select>
                                    <button
                                        type="button"
                                        disabled={
                                            !reassignTarget || reassignBusy
                                        }
                                        onClick={async () => {
                                            if (
                                                !window.confirm(
                                                    'Move this activation — with its gallery, media, and shares — to the selected organization?',
                                                )
                                            )
                                                return;
                                            setReassignBusy(true);
                                            const r =
                                                await reassignActivationOrg(
                                                    id,
                                                    +reassignTarget,
                                                );
                                            setReassignBusy(false);
                                            if (r?.success)
                                                navigate(
                                                    '/v2/activations',
                                                );
                                        }}
                                        className="cursor-pointer rounded-full border-0 bg-foreground px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-background disabled:opacity-50"
                                    >
                                        {reassignBusy
                                            ? 'Moving…'
                                            : 'Reassign'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setReassignOpen(false)
                                        }
                                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-muted-foreground"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        setReassignOpen(true);
                                        if (reassignOrgs == null)
                                            setReassignOrgs(
                                                await listOrgs().catch(
                                                    () => [],
                                                ),
                                            );
                                    }}
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-faint hover:text-attention"
                                >
                                    Reassign organization…
                                </button>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className="group mb-9 mt-2">
                    <h1 className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[23px] font-bold leading-tight tracking-[-0.02em]">
                        {activation?.name ?? 'Activation'}
                        {activation?.isArchived && (
                            <button
                                type="button"
                                title="Unarchive this activation"
                                onClick={async () => {
                                    if (
                                        !window.confirm(
                                            'Unarchive this activation?',
                                        )
                                    )
                                        return;
                                    const ok = await unarchiveActivation(
                                        id,
                                    ).catch(() => false);
                                    if (ok)
                                        setActivation((a: any) =>
                                            a
                                                ? {
                                                      ...a,
                                                      isArchived: false,
                                                  }
                                                : a,
                                        );
                                }}
                                className="cursor-pointer rounded-full border-0 bg-secondary px-2 py-0.5 font-[inherit] text-[10.5px] font-semibold text-muted-foreground hover:text-foreground"
                            >
                                archived · unarchive
                            </button>
                        )}
                        <button
                            type="button"
                            title="Edit details"
                            onClick={startEditing}
                            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent text-faint opacity-0 transition-opacity hover:bg-secondary hover:text-foreground focus:opacity-100 group-hover:opacity-100"
                        >
                            <svg
                                viewBox="0 0 20 20"
                                className="h-[15px] w-[15px]"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M13.5 3.5 16.5 6.5 7 16H4v-3z" />
                            </svg>
                        </button>
                    </h1>
                    <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-[13px] text-muted-foreground">
                        {d && <span>{d.format('MMM D, YYYY')}</span>}
                        {activation?.location && (
                            <>
                                <span className="text-faint">·</span>
                                <span>{activation.location}</span>
                            </>
                        )}
                        {activation?.owner && (
                            <>
                                <span className="text-faint">·</span>
                                <span>Lead: {activation.owner}</span>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* KPIs — borderless */}
            <div className="mb-10 flex flex-wrap gap-11">
                <div>
                    <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                        Photos
                        <span className="h-[7px] w-[7px] rounded-full bg-brand" />
                    </div>
                    <div className="mt-1.5 text-[28px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
                        {kpis.photos.toLocaleString()}
                    </div>
                </div>
                <div>
                    <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                        Sessions
                        <span className="h-[7px] w-[7px] rounded-full bg-faint" />
                    </div>
                    <div className="mt-1.5 text-[28px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
                        {kpis.sessions.toLocaleString()}
                    </div>
                </div>
                <div>
                    <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                        Shares
                        <span className="h-[7px] w-[7px] rounded-full bg-attention" />
                    </div>
                    <div className="mt-1.5 text-[28px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
                        {kpis.shares.toLocaleString()}
                    </div>
                    {kpis.unsent > 0 && (
                        <div className="mt-1.5 text-[11px] font-semibold text-attention">
                            {kpis.unsent} unsent
                        </div>
                    )}
                </div>
                <div>
                    <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                        Views
                        <span className="h-[7px] w-[7px] rounded-full bg-faint" />
                    </div>
                    <div className="mt-1.5 text-[28px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
                        {kpis.views.toLocaleString()}
                    </div>
                </div>
            </div>

            {/* hero chart — flows past the content grid (ref: Visitors) */}
            {chart && (
                <>
                    <HeroChart series={chart.series} axis={chart.axis} />
                    <div className="my-9 h-px bg-line" />
                </>
            )}

            {/* live-ops: devices uploading to this event */}
            <EventDevices activationId={id} />

            <div className="grid gap-10 md:grid-cols-2">
                {/* gallery links */}
                <section>
                    <div className="ps-label mb-2">Gallery links</div>
                    {galleries.length === 0 && (
                        <p className="py-3 text-[13px] text-muted-foreground">
                            No gallery yet.
                        </p>
                    )}
                    {galleries.map((g: any, gi: number) => (
                        <div key={g.id ?? gi}>
                            {[
                                { t: 'Public gallery', path: '' },
                                { t: 'Sharing station', path: '/sharing' },
                                { t: 'Slideshow', path: '/slideshow' },
                                { t: 'Analyze lookup', path: null as any },
                            ].map((link) => {
                                const href = g.slug
                                    ? link.path == null
                                        ? `${galleryUrl}/lookup/${encodeURIComponent(g.slug)}`
                                        : `${galleryUrl}/gallery/${g.slug}${link.path}`
                                    : null;
                                return (
                                    <div
                                        key={link.t}
                                        className="flex items-center gap-2.5 border-b border-line py-2 text-[12.5px] last:border-b-0"
                                    >
                                        <span className="font-semibold">
                                            {link.t}
                                        </span>
                                        {galleries.length > 1 && (
                                            <span className="text-faint">
                                                {g.name}
                                            </span>
                                        )}
                                        {href ? (
                                            <a
                                                href={href}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="ml-auto max-w-[240px] truncate font-mono text-[10.5px] text-brand-ink hover:underline"
                                            >
                                                {href.replace(
                                                    /^https?:\/\//,
                                                    '',
                                                )}
                                            </a>
                                        ) : (
                                            <span className="ml-auto text-[11px] text-faint">
                                                —
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </section>

                {/* recent sessions */}
                <section>
                    <div className="ps-label mb-2">Recent sessions</div>
                    {recentSessions.length === 0 && (
                        <p className="py-3 text-[13px] text-muted-foreground">
                            No sessions yet — they'll appear as devices
                            capture.
                        </p>
                    )}
                    {recentSessions.map((s, i) => (
                        <div
                            key={s.id ?? i}
                            className="flex items-baseline gap-2.5 border-b border-line py-2 text-[12.5px] last:border-b-0"
                        >
                            <span className="font-semibold tabular-nums">
                                {s.total ?? 0} photo
                                {Number(s.total) === 1 ? '' : 's'}
                            </span>
                            {s.destination && (
                                <span className="max-w-[200px] truncate text-muted-foreground">
                                    → {s.destination}
                                </span>
                            )}
                            <span className="ml-auto whitespace-nowrap text-[11.5px] text-faint">
                                {moment(s.date).fromNow()}
                            </span>
                        </div>
                    ))}
                </section>
            </div>

            {/* the activation's job data — schedule, venue, crew, booth,
                gear, contacts, design, comms, timeline */}
            <ActivationOpsSections activationId={id} />
        </main>
    );
}
